// Swimlane trace: vertical lifelines for threads / transactions / keys, grouped
// into three bands, with lanes reused once their object dies. Each event is one
// row: a horizontal connector links the participating lifelines (a circle on
// each), and five text columns spell out timestamp, op, txn, key, and value.
//
// Interactions:
//  - click a row (or Up/Down) to select it — the source line that produced it
//    lights up in the editor (driven by the parent via onSelectRow);
//  - click a point on a lifeline to move the cursor to that (row, lane): the
//    row is selected and the lane it crosses is filtered to that thread / txn /
//    key, every row it did not take part in fading. With a row selected, Left/
//    Right cycle through only the lifelines that event touches; with no row
//    selected they step to the adjacent lifeline track, scrolling it into view.
//    Re-clicking the cursor cell, Esc, or the summary chip clears the filter.
//  - drag a row up or down to overrule the scheduler and re-run the program
//    under that interleaving. Rows group into scheduler *steps* (hairline-
//    separated) and a whole step moves at once, since the events inside one had
//    no interleaving point between them. The drag is live and sticky: the rows
//    stay put and lean towards the pointer until it passes halfway to the next
//    position the step could occupy, at which point the program re-runs and they
//    snap there. Positions it can never occupy fade. See ../drag.ts,
//    docs/design/trace-reordering.md, and docs/concurrency.md#steps-ticks-and-rows
//    for why the step count in the summary exceeds the number of rows.

import { useEffect, useMemo, useRef, useState } from "react";
import type { RunResult } from "../../src/backend/interp/interpreter.js";
import type { Causality, StepId } from "../../src/backend/interp/causality.js";
import { dropSlots, pullOffset, slotFor } from "../drag.js";
import type { Slot } from "../drag.js";
import { stepOfRow, toReorder } from "../reorder.js";
import type { Reorder } from "../reorder.js";
import {
  eventColumns,
  eventTxnId,
  GROUP_LABEL,
  GROUP_ORDER,
  HEAD_H,
  Layout,
  MID,
  ObjectTypes,
  ROW_H,
  txnOutcomes,
} from "../trace-layout.js";
import { useVirtualRows } from "./useVirtualRows.js";

interface TraceViewProps {
  result: RunResult;
  layout: Layout;
  types?: ObjectTypes;
  selectedRow: number;
  activeLane: number;
  onSelectRow(row: number): void;
  onToggleLane(lane: number): void;
  onClearFilter(): void;
  /** The reordering the drag currently describes, re-run and re-rendered as the
   *  pointer moves; null when the pointer is back at the step's own position.
   *  Absent (with `result.causality` null) when reordering isn't available. */
  onLiveReorder?(reorder: Reorder | null): void;
  /** The pointer went up: keep whatever `onLiveReorder` last described. */
  onCommitReorder?(): void;
}

/** Pointer travel before a press becomes a drag, so a click that trembles is
 *  still a click. */
const ARM_PX = 3;

function BandRects({ layout, height }: { layout: Layout; height: number }) {
  return (
    <>
      {layout.bands.map((b) => (
        <rect
          key={`${b.group}-${b.subgroup ?? ""}`}
          className={`band band-${b.group}`}
          x={b.left}
          y={0}
          width={b.right - b.left}
          height={height}
        />
      ))}
    </>
  );
}

function LaneHeader({ layout }: { layout: Layout }) {
  const W = layout.width;
  return (
    <svg className="lanes-head" width={W} height={HEAD_H} viewBox={`0 0 ${W} ${HEAD_H}`}>
      <BandRects layout={layout} height={HEAD_H} />
      {/* Top row: the three group labels. "keys" spans all of its sub-bands. */}
      {GROUP_ORDER.map((g) => {
        const span = layout.groupSpan[g];
        if (!span) return null;
        const cx = (span.x0 + span.x1) / 2;
        return (
          <g key={g}>
            <text className="grp-label" x={cx} y={11}>
              {GROUP_LABEL[g]}
            </text>
            <line className="grp-rule" x1={span.x0 - 5} y1={17} x2={span.x1 + 5} y2={17} />
          </g>
        );
      })}
      {/* Second row: one label per key sub-band (its object type). */}
      {layout.keySubBands
        .filter((sb) => sb.type)
        .map((sb) => {
          const cx = (sb.x0 + sb.x1) / 2;
          return (
            <g key={sb.type}>
              <text className="grp-sublabel" x={cx} y={31}>
                {sb.type}
              </text>
              <line className="grp-rule" x1={sb.x0 - 4} y1={36} x2={sb.x1 + 4} y2={36} />
            </g>
          );
        })}
    </svg>
  );
}

const LANE_HIT_W = 15;

function RowLanes({
  row,
  layout,
  activeLane,
  cursorLane,
  onToggleLane,
}: {
  row: number;
  layout: Layout;
  activeLane: number;
  cursorLane: number; // lane to box as the cursor in this row (selected row only), or -1
  onToggleLane(lane: number): void;
}) {
  const W = layout.width;
  const segs = layout.perRowLanes[row]!;
  const info = layout.rows[row]!;
  const xs = info.parts.map((i) => layout.x[i]!);
  const min = Math.min(...xs);
  const max = Math.max(...xs);
  // The cursor cell: where the selected row crosses the active lifeline. Only
  // drawn when that lane actually passes through this row.
  const showCursor = cursorLane >= 0 && segs.some((s) => s.lane === cursorLane);

  return (
    <svg className="lanes" width={W} height={ROW_H} viewBox={`0 0 ${W} ${ROW_H}`}>
      <BandRects layout={layout} height={ROW_H} />
      {showCursor && (
        <rect
          className="lane-cursor"
          x={layout.x[cursorLane]! - LANE_HIT_W / 2}
          y={1}
          width={LANE_HIT_W}
          height={ROW_H - 2}
        />
      )}

      {/* Vertical lifeline segments passing through this row. Threads and keys
          are neutral grey; a txn lane is coloured by how it ends (green =
          commit, red = abort) so its fate is legible without scrolling. */}
      {segs.map((seg) => {
        const lane = layout.lanes[seg.lane]!;
        const variant = lane.group === "txn" && lane.outcome ? `txn-${lane.outcome}` : lane.group;
        const cx = layout.x[seg.lane]!;
        const base = `ll ll-${variant}${seg.lane === activeLane ? " lane-on" : ""}`;
        // Split at MID into two halves so a blocked span can be dotted from the
        // event circle, not the row edge. A lane born/dying this row omits the
        // half it doesn't occupy.
        return (
          <g key={seg.lane}>
            {!seg.isBirth && (
              <line
                className={`${base}${seg.blockedTop ? " ll-blocked" : ""}`}
                x1={cx}
                y1={0}
                x2={cx}
                y2={MID}
              />
            )}
            {!seg.isDeath && (
              <line
                className={`${base}${seg.blockedBottom ? " ll-blocked" : ""}`}
                x1={cx}
                y1={MID}
                x2={cx}
                y2={ROW_H}
              />
            )}
            {seg.isBirth && (
              <text className={`lane-label lane-label-${variant}`} x={cx} y={6}>
                {lane.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Horizontal connector + a circle on each participating lifeline. */}
      {xs.length > 1 && (
        <line className="conn" stroke={info.color} x1={min} y1={MID} x2={max} y2={MID} />
      )}
      {xs.map((cx, i) => (
        <circle key={i} cx={cx} cy={MID} r={3.4} fill={info.color} />
      ))}

      {/* Transparent hit targets (on top) so clicking anywhere down a lifeline's
          column moves the cursor to that (row, lane): the click bubbles to the
          row for its vertical position + focus, and here we set the column.
          Re-clicking the current cursor cell clears the filter. */}
      {segs.map((seg) => {
        const cx = layout.x[seg.lane]!;
        const lane = layout.lanes[seg.lane]!;
        return (
          <rect
            key={`hit-${seg.lane}`}
            className={`lane-hit${seg.lane === activeLane ? " lane-on" : ""}`}
            x={cx - LANE_HIT_W / 2}
            y={0}
            width={LANE_HIT_W}
            height={ROW_H}
            onClick={() => {
              // Set the lane when it differs; on the current cursor cell (this
              // row is selected — cursorLane >= 0 — and it's the active lane),
              // toggle it back off. Same lane on another row: leave it, and let
              // the bubbled row click move the cursor down/up the lifeline.
              if (seg.lane !== activeLane || cursorLane >= 0) onToggleLane(seg.lane);
            }}
          >
            <title>{lane.label}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export function TraceView({
  result,
  layout,
  types,
  selectedRow,
  activeLane,
  onSelectRow,
  onToggleLane,
  onClearFilter,
  onLiveReorder,
  onCommitReorder,
}: TraceViewProps) {
  const events = result.trace.events;
  const outcomes = useMemo(() => txnOutcomes(events), [events]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { start, end, padTop, padBottom } = useVirtualRows(scrollRef, events.length, ROW_H);

  // --- reordering ----------------------------------------------------------
  // A drag is a live gesture, not a drop: pull a step more than halfway towards
  // the next position it could occupy and the program re-runs under that
  // schedule straight away. See `../drag.ts` for the geometry and the note in
  // `onPointerDown` for why this is hand-rolled rather than HTML5 drag-and-drop.
  const causality = result.causality;
  const canReorder = !!causality && !!onLiveReorder;

  // Live props, read from inside pointer handlers that outlive the render that
  // installed them (the gesture spans many re-runs, each a new render).
  const live = useRef({ onLiveReorder, onCommitReorder });
  live.current = { onLiveReorder, onCommitReorder };

  // The gesture, kept in a ref so pointermove can run at pointer rate without a
  // React render. Only `picked` — the step id under the hand — is state, and it
  // changes twice a gesture rather than every pixel.
  const drag = useRef<{
    moved: number; // index into `baseline.steps`
    baseline: Causality;
    slots: Slot[];
    legal: Set<StepId>; // slot targets by id, so fading survives a re-run
    /** The press, and where the pointer was when the gesture last settled (the
      *  press, or the most recent snap — the lean is measured from there). Both
      *  in the trace's frame; see `frameY`. */
    startY: number;
    anchorY: number;
    slot: number;
    armed: boolean;
  } | null>(null);
  const [picked, setPicked] = useState<StepId | null>(null);
  // The step under the cursor, and whether it has anywhere to go. A whole step
  // moves at once, so the trace says which rows that is *before* the press —
  // "why did three rows move?" should never be a question you can only answer by
  // having moved them. Pinned steps say so too, by not offering a grab cursor.
  const [hover, setHover] = useState<{ step: number; movable: boolean } | null>(null);
  /** Tears down the gesture in flight, if any. Held so unmounting mid-drag —
   *  which would otherwise leave the window listeners installed for good — ends
   *  it like a cancel. */
  const finishRef = useRef<((commit: boolean) => void) | null>(null);
  useEffect(() => () => finishRef.current?.(false), []);
  // Set after a gesture that actually moved something, so the click that ends it
  // doesn't also re-select a row the user was only dragging.
  const dragged = useRef(false);

  // The picked-up step in the run *currently* on screen. Each snap re-runs the
  // program and renumbers everything, but a step id is stable across exactly the
  // reorderings that don't change that thread's history — which is what makes it
  // possible to keep hold of a row through the re-runs the drag itself causes.
  const pickedStep = picked && causality ? causality.steps.findIndex((s) => s.id === picked) : -1;

  /** How far the picked-up rows lean, in px — the give that says "held, and
   *  pulling this way" without pretending the step can land anywhere. Written
   *  straight to a CSS variable, since it changes with every pointermove. */
  const setPull = (px: number): void => {
    scrollRef.current?.style.setProperty("--pull", `${px}px`);
  };

  /**
   * A pointer position in the frame the slots are measured in: the trace's own
   * scrolled content, rather than the viewport.
   *
   * Adding `scrollTop` means scrolling the trace under a held step counts as
   * dragging it, which is what a reader expects and what an auto-scrolling drag
   * would need. Not subtracting the container's top means the opposite for
   * layout: if something above the trace changes height mid-gesture, the ground
   * moves but the gesture does not, so a shift can never be mistaken for a pull
   * the user's hand didn't make. (The strip above is fixed-height so that the
   * rows don't visibly slide either — this only guarantees the arithmetic.)
   */
  const frameY = (clientY: number): number => clientY + (scrollRef.current?.scrollTop ?? 0);

  const endGesture = (commit: boolean): void => {
    if (drag.current?.armed) {
      dragged.current = true;
      if (commit) live.current.onCommitReorder?.();
      else live.current.onLiveReorder?.(null); // Esc: back to where we started
    }
    drag.current = null;
    setPicked(null);
    setPull(0);
  };

  const onHoverRow = (row: number): void => {
    if (drag.current || !causality) return; // mid-gesture the picked step owns the highlight
    const step = stepOfRow(causality, row);
    if (step < 0 || step === hover?.step) return;
    setHover({ step, movable: dropSlots(causality, step).length > 1 });
  };

  const onPointerDown = (row: number, e: React.PointerEvent): void => {
    // Hand-rolled rather than HTML5 drag-and-drop, which cannot express this
    // gesture: the browser drags a ghost image of the row (so the row would
    // appear to move before it snaps), and re-rendering the dragged element —
    // which every snap does — cancels a native drag outright, ending the gesture
    // after the first one.
    if (!canReorder || !causality || e.button !== 0) return;
    // Consumed here rather than by the click that ends a drag: after a snap the
    // press and the release are on different rows, so the browser dispatches the
    // click on their common ancestor and no row handler ever sees it. Left set,
    // the flag would swallow the *next* real click instead.
    dragged.current = false;
    const step = stepOfRow(causality, row);
    if (step < 0) return;
    const slots = dropSlots(causality, step);
    if (slots.length < 2) return; // nowhere it could go; leave the row to click
    drag.current = {
      moved: step,
      baseline: causality,
      slots,
      legal: new Set(slots.map((s) => causality.steps[s.target]!.id)),
      startY: frameY(e.clientY),
      anchorY: frameY(e.clientY),
      slot: slots.findIndex((s) => s.offset === 0),
      armed: false,
    };

    const onMove = (ev: PointerEvent): void => {
      const g = drag.current;
      if (!g) return;
      const y = frameY(ev.clientY);
      const dy = y - g.startY;
      // A few pixels of slack first, so a click that trembles is still a click.
      if (!g.armed) {
        if (Math.abs(dy) < ARM_PX) return;
        g.armed = true;
        setPicked(g.baseline.steps[g.moved]!.id);
      }
      const next = slotFor(g.slots, dy, ROW_H, g.slot);
      if (next === g.slot) {
        setPull(pullOffset(y - g.anchorY));
        return;
      }
      g.slot = next;
      g.anchorY = y;
      setPull(0);
      const slot = g.slots[next]!;
      // The gesture owns exactly one reordering and replaces it as the pointer
      // travels, rather than stacking one per snap: the user made one gesture,
      // so the strip should show one chip — and dragging back to where you
      // started should leave no trace of it at all, which `null` does.
      live.current.onLiveReorder?.(
        slot.offset === 0 ? null : toReorder(g.baseline, g.moved, slot.target)
      );
    };
    const finish = (commit: boolean): void => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey, true);
      finishRef.current = null;
      endGesture(commit);
    };
    finishRef.current = finish;
    const onUp = (): void => finish(true);
    const onCancel = (): void => finish(false);
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key !== "Escape") return;
      // Swallowed, so the same Escape doesn't also clear the lane filter.
      ev.preventDefault();
      ev.stopPropagation();
      finish(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey, true);
  };

  const rowMatchesFilter = (row: number): boolean =>
    activeLane < 0 || (layout.rows[row]?.parts.includes(activeLane) ?? true);

  // Distinct lifeline x positions ("tracks"), left to right. A single track can
  // host several lanes over time (a column is reused once its lane dies), so
  // track-stepping and lane-stepping are different: Left/Right walk tracks when
  // nothing is selected, but walk the selected event's own lanes when a row is.
  const tracks = useMemo(() => [...new Set(layout.x)].sort((a, b) => a - b), [layout]);

  const laneRows = (idx: number): [number, number] => {
    const lane = layout.lanes[idx]!;
    return [lane.birth, lane.death < 0 ? events.length - 1 : lane.death];
  };

  // The row nearest the middle of the viewport — used to pick which lane on a
  // freshly-entered track is most relevant, and where to scroll to reveal it.
  const viewportCenterRow = (): number => {
    const c = scrollRef.current;
    if (!c) return 0;
    const headH = (c.querySelector(".thead") as HTMLElement | null)?.offsetHeight ?? 0;
    return Math.round((c.scrollTop - headH + c.clientHeight / 2) / ROW_H);
  };

  // Of the lanes sharing track x, the one whose lifespan is closest to `center`.
  const pickLaneOnTrack = (x: number, center: number): number => {
    let best = -1;
    let bestDist = Infinity;
    layout.lanes.forEach((_, i) => {
      if (layout.x[i] !== x) return;
      const [birth, death] = laneRows(i);
      const dist = center < birth ? birth - center : center > death ? center - death : 0;
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  };

  // Scroll a lane's lifeline into view if it sits entirely above or below the
  // viewport (its column may have been born far from where we're looking).
  const revealLane = (idx: number): void => {
    const container = scrollRef.current;
    if (!container) return;
    const headH = (container.querySelector(".thead") as HTMLElement | null)?.offsetHeight ?? 0;
    const [birth, death] = laneRows(idx);
    const spanTop = headH + birth * ROW_H;
    const spanBottom = headH + (death + 1) * ROW_H;
    if (spanBottom <= container.scrollTop + headH || spanTop >= container.scrollTop + container.clientHeight) {
      container.scrollTop = spanTop - headH; // bring the lane's birth just under the sticky header
    }
  };

  // Scroll the selected row into view whenever it changes. Windowing means the
  // row's element may not be mounted (arrow-nav under a filter can jump past the
  // overscan margin), and the sticky header would eclipse a row parked at the
  // very top. Both rule out `scrollIntoView`, so compute from the row's fixed
  // height instead — a "nearest"-style nudge that only scrolls when the row is
  // off-screen, and keeps it clear of the sticky header when scrolling up.
  useEffect(() => {
    if (selectedRow < 0) return;
    // Not mid-gesture: every snap re-runs the program, which can renumber the
    // selected row, and scrolling to it would drag the trace out from under the
    // hand that is holding a step.
    if (drag.current) return;
    const container = scrollRef.current;
    if (!container) return;
    const headH = (container.querySelector(".thead") as HTMLElement | null)?.offsetHeight ?? 0;
    const rowTop = headH + selectedRow * ROW_H;
    if (rowTop < container.scrollTop + headH) {
      container.scrollTop = rowTop - headH; // clear the sticky header
    } else if (rowTop + ROW_H > container.scrollTop + container.clientHeight) {
      container.scrollTop = rowTop + ROW_H - container.clientHeight;
    }
  }, [selectedRow]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const dir = e.key === "ArrowDown" ? 1 : -1;
      const from = selectedRow < 0 ? (dir === 1 ? -1 : events.length) : selectedRow;
      // Skip rows the active filter greyed out.
      for (let r = from + dir; r >= 0 && r < events.length; r += dir) {
        if (rowMatchesFilter(r)) {
          onSelectRow(r);
          break;
        }
      }
    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      const selEvent = selectedRow >= 0 ? layout.rows[selectedRow] : undefined;
      if (selEvent) {
        // A row is selected: step only through the lifelines that event touches
        // (its thread, txn, and object), in left-to-right order, stopping at the
        // ends. The row is already in view, so no scrolling is needed.
        const parts = [...selEvent.parts].sort((a, b) => layout.x[a]! - layout.x[b]!);
        const pos = parts.indexOf(activeLane);
        if (pos < 0) onToggleLane(dir === 1 ? parts[0]! : parts[parts.length - 1]!);
        else if (pos + dir >= 0 && pos + dir < parts.length) onToggleLane(parts[pos + dir]!);
        return;
      }
      // No row selected: step to the adjacent track and select the lane on it
      // nearest what we're looking at, scrolling to reveal it if it's off-screen.
      if (tracks.length === 0) return;
      const curX = activeLane >= 0 ? layout.x[activeLane]! : null;
      const ti = (curX === null ? (dir === 1 ? -1 : tracks.length) : tracks.indexOf(curX)) + dir;
      if (ti < 0 || ti >= tracks.length) return;
      const next = pickLaneOnTrack(tracks[ti]!, viewportCenterRow());
      if (next < 0) return;
      onToggleLane(next);
      revealLane(next);
    } else if (e.key === "Escape" && activeLane >= 0) {
      e.preventDefault();
      onClearFilter();
    }
  };

  const lanew = `${layout.width}px`;

  return (
    <div
      id="trace"
      ref={scrollRef}
      className={`${activeLane >= 0 ? "filtering" : ""}${picked ? " dragging-step" : ""}`.trim() || undefined}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerLeave={() => setHover(null)}
    >
      <div className="ttable">
        <div className="thead" style={{ ["--lanew" as string]: lanew }}>
          <div className="lanecell">
            <LaneHeader layout={layout} />
          </div>
          <span>t</span>
          <span>op</span>
          <span>txn</span>
          <span>key</span>
          <span>value</span>
        </div>

        {/* Only the rows intersecting the viewport are mounted; the spacers
            reserve the scroll height of the rows above and below the window. */}
        {padTop > 0 && <div className="trow-spacer" style={{ height: padTop }} aria-hidden />}
        {events.slice(start, end).map((ev, i) => {
          const row = start + i;
          const { op, opCls, txn, key, value } = eventColumns(ev, types);
          const tid = eventTxnId(ev);
          const txnCls = tid !== null && outcomes.has(tid) ? ` c-txn-${outcomes.get(tid)}` : "";
          // Scheduler-step bookkeeping: a hairline opens each new step group,
          // the picked-up group leans towards the pointer, and positions the
          // step can never occupy fade. Which rows are which is answered by step
          // *id*, since a snap re-runs the program and renumbers every step.
          const step = causality ? stepOfRow(causality, row) : -1;
          const stepCls =
            (causality && row > 0 && stepOfRow(causality, row - 1) !== step ? " grp" : "") +
            (pickedStep >= 0
              ? step === pickedStep
                ? " picked"
                : drag.current?.legal.has(causality!.steps[step]!.id)
                  ? ""
                  : " nodrop"
              : step === hover?.step
                ? hover.movable
                  ? " step-hover movable"
                  : " step-hover"
                : "");
          const classes =
            "trow" +
            (row === selectedRow ? " sel" : "") +
            (activeLane >= 0 && !rowMatchesFilter(row) ? " dimmed" : "") +
            stepCls;
          return (
            <div
              key={row}
              className={classes}
              data-row={row}
              style={{ ["--lanew" as string]: lanew }}
              onPointerEnter={() => onHoverRow(row)}
              onPointerDown={(e) => onPointerDown(row, e)}
              // A row is a drag surface, so nothing in it may start a *native*
              // drag: one press that lands on selected text is enough for the
              // browser to take the pointer over and fire `pointercancel` at
              // us, ending the gesture on its first move. (Rows also carry
              // `user-select: none` — the two together are what the `draggable`
              // attribute used to do implicitly.)
              onDragStart={(e) => e.preventDefault()}
              onClick={(e) => {
                // The click that ends a drag is not a click on a row.
                if (dragged.current) {
                  dragged.current = false;
                  return;
                }
                (e.currentTarget.closest("#trace") as HTMLElement)?.focus();
                onSelectRow(row);
              }}
            >
              <div className="lanecell">
                <RowLanes
                  row={row}
                  layout={layout}
                  activeLane={activeLane}
                  cursorLane={row === selectedRow ? activeLane : -1}
                  onToggleLane={onToggleLane}
                />
              </div>
              <span className="c-time">{ev.time.toFixed(2)}</span>
              <span className={`c-op ${opCls}`}>{op}</span>
              <span className={`c-txn${txnCls}`}>{txn}</span>
              <span className="c-key">{key}</span>
              <span className="c-val">{value}</span>
            </div>
          );
        })}
        {padBottom > 0 && <div className="trow-spacer" style={{ height: padBottom }} aria-hidden />}
      </div>
    </div>
  );
}
